/**
 * Reads one row's figure out of every column of a multi-period report,
 * substituting an explicit `0` for a column the key is absent from.
 *
 * **A missing column is a genuine 0.00, never an absence of information.**
 * Each column is a complete statement for its own window, so a key that does
 * not appear in one simply had no movement in that window - which on a
 * financial statement is a fact, and the fact is zero. Rendering it blank
 * would invite the reader to take "zero" for "no data", and on a statement
 * that is a misstatement rather than a display quirk. The balance sheet
 * series has always rendered these as zeros; the income statement and both
 * cash-flow methods read the same rule from here, so the three agree by
 * construction rather than by three renderers happening to match.
 *
 * Callers hold one `Map` per column, all keyed alike (a ledger account id, a
 * cash-flow line key), and get back one figure per column in column order.
 */
export function seriesAmountsForKey(amountsByColumn, key) {
    return amountsByColumn.map((amounts) => amounts.get(key) ?? 0);
}
/**
 * The union-by-key engine shared by every series merge: {@link mergeSeriesRows}
 * and {@link mergeSeriesGroupNodes} both need the same three steps - union a
 * per-column value by a caller-supplied key, remember the first row to define
 * each key (for its descriptive fields), and sort the result by a
 * caller-supplied comparator over that first row - and differ only in WHAT
 * they carry forward per column. `mergeSeriesRows` carries a single number
 * (a balance, later zero-filled and dropped from the row); `mergeSeriesGroupNodes`
 * carries the whole node, because it must recurse into a node's own subgroups
 * and accounts rather than reading one figure off it. `valueOf` is that split
 * point: it is `(row) => row.balanceMinorUnits` for the former and the
 * identity function for the latter, and `build` gets the per-column value maps
 * so it can zero-fill a scalar or recurse into a substructure as its own case
 * requires.
 */
function mergeColumnsByKey(rowsByColumn, keyOf, valueOf, compare, build) {
    const rowsByKey = new Map();
    const valuesByColumn = rowsByColumn.map((rows) => {
        const values = new Map();
        for (const row of rows) {
            const key = keyOf(row);
            if (!rowsByKey.has(key)) {
                rowsByKey.set(key, row);
            }
            values.set(key, valueOf(row));
        }
        return values;
    });
    return [...rowsByKey.entries()]
        .sort((a, b) => compare(a[1], b[1]))
        .map(([key, row]) => build(row, key, valuesByColumn));
}
/**
 * Unions the rows of every column into one row set, each carrying one figure
 * per column.
 *
 * The union is by `keyOf`, and the first column to define a key supplies that
 * row's descriptive fields. Those fields describe the THING (an account's code
 * and name, a client's name), not the date, so any column that has the row
 * agrees about them; taking the first avoids a needless "which column is
 * authoritative" question that has no observable answer.
 *
 * A column missing the row contributes 0 for that cell - see
 * {@link seriesAmountsForKey} for why a missing column is a genuine zero on a
 * financial statement rather than a blank.
 */
export function mergeSeriesRows(rowsByColumn, keyOf, compare) {
    return mergeColumnsByKey(rowsByColumn, keyOf, (row) => row.balanceMinorUnits, compare, (row, key, balancesByColumn) => {
        const { balanceMinorUnits: _dropped, ...rest } = row;
        return {
            ...rest,
            amountsMinorUnits: seriesAmountsForKey(balancesByColumn, key),
        };
    });
}
/** {@link mergeSeriesRows} for ledger accounts: keyed by account id, ordered by code. */
export function mergeSeriesAccounts(accountsByColumn) {
    return mergeSeriesRows(accountsByColumn, (account) => account.ledgerAccountId, (a, b) => a.code.localeCompare(b.code));
}
/**
 * Unions one level of an account-group tree across columns (by group id,
 * ordered by `sortIndex`), then recurses into each merged group's own
 * subgroups and accounts.
 *
 * A group absent from a column contributes a zero subtotal cell for that
 * column and no subgroups or accounts at that level - the same rule
 * {@link seriesAmountsForKey} states for a leaf, applied to a node: a column
 * that does not carry the group held nothing in it, which on a statement is a
 * figure of zero rather than a blank. The first column to define a group
 * supplies its name and `sortIndex`, which describe the group rather than the
 * date, so every column that has it agrees about them.
 *
 * Shares its union-by-key mechanics with {@link mergeSeriesRows} via
 * {@link mergeColumnsByKey}; it carries the whole node per column, rather than
 * a single figure, because it must recurse into a node's own `groups` and
 * `accounts` rather than reading one balance off it.
 */
export function mergeSeriesGroupNodes(nodesByColumn) {
    return mergeColumnsByKey(nodesByColumn, (node) => node.id, (node) => node, (a, b) => a.sortIndex - b.sortIndex, (node, id, nodesByColumn) => ({
        id,
        name: node.name,
        sortIndex: node.sortIndex,
        subtotalMinorUnits: nodesByColumn.map((byColumn) => byColumn.get(id)?.subtotalMinorUnits ?? 0),
        groups: mergeSeriesGroupNodes(nodesByColumn.map((byColumn) => byColumn.get(id)?.groups ?? [])),
        accounts: mergeSeriesAccounts(nodesByColumn.map((byColumn) => byColumn.get(id)?.accounts ?? [])),
    }));
}
/** Whether `later` is the month end immediately following `earlier`'s month end. */
function isConsecutiveMonthEnd(earlier, later) {
    const [earlierYear, earlierMonth] = earlier.split('-').map(Number);
    const nextMonthEnd = new Date(Date.UTC(earlierYear, earlierMonth + 1, 0)).toISOString().slice(0, 10);
    return later === nextMonthEnd;
}
/**
 * One column per date an as-at series states a position at, in the order the
 * series returns them (oldest first, anchor last).
 *
 * Labelled by DATE, never by month name: a column's date is not always a month
 * end - the anchor can be any day - and "August 2026" over a column dated the
 * 2nd would misstate what the figures are as at.
 *
 * `separator` draws a rule wherever a column is NOT the month end immediately
 * following its neighbour, which happens in two ways and is wanted in both. A
 * `prior-year` comparison jumps a year. A mid-month anchor ends a run of month
 * ends with a partial month - "30 Jun, 31 Jul, 31 Aug, 3 Sep" - and the rule is
 * what stops the last column reading as another completed month. A series whose
 * anchor IS a month end is one unbroken walk and carries no rule at all.
 *
 * Shared by every as-at series (balance sheet, trial balance, trust
 * reconciliation): the three are read side by side as at the same dates, so
 * they must label and rule those dates identically.
 */
export function asOfSeriesColumns(statements, formatLabel) {
    const dates = statements.map((statement) => statement.asOf.slice(0, 10));
    return statements.map((statement, index) => ({
        label: formatLabel(dates[index]),
        statement,
        separator: index > 0 && !isConsecutiveMonthEnd(dates[index - 1], dates[index]),
    }));
}
/**
 * Horizontal page margin (each side) every report PDF renders, in points.
 *
 * Lives beside the series fits because they are what subtracts it: a page
 * whose padding and whose column-fit arithmetic disagreed would either clip a
 * money column or refuse a series that fits.
 * `document-rendering/pdf/styles.ts` reads it for the page padding itself.
 */
export const REPORT_PDF_HORIZONTAL_MARGIN_PT = 48;
/**
 * A4 landscape geometry for an as-at series PDF: a code column, a name column,
 * and one money column per date.
 *
 * Named and homed here for the three as-at series exports (balance sheet,
 * trial balance, trust reconciliation), which are what `asOfSeriesColumns`
 * above builds their columns for. The income-statement series is a PERIOD
 * report, not an as-at one, and builds its columns from a date range rather
 * than `asOfSeriesColumns` - but it prints the identical table shape (a code
 * column, a name column, one money column per column), so it deliberately
 * reuses this geometry rather than defining a second, identical constant that
 * could only ever drift from this one. See `INCOME_STATEMENT_SERIES_PDF_FIT`
 * in `income-statement-series-presentation.ts`, which aliases this export
 * under the income statement's own name.
 *
 * A series wider than the page fits is refused at export with a message
 * pointing at XLSX, rather than being silently clipped - a truncated
 * financial statement is worse than none, because nothing on the page says a
 * column is missing.
 */
export const AS_OF_SERIES_PDF_FIT = {
    a4LandscapeWidthPt: 841.89,
    codeColumnWidthPt: 46,
    amountColumnWidthPt: 70,
    minNameColumnWidthPt: 130,
    horizontalMarginPt: REPORT_PDF_HORIZONTAL_MARGIN_PT,
};
/** How many dated money columns fit across an A4 landscape page. */
export function maxAsOfSeriesPdfColumns() {
    const { a4LandscapeWidthPt, codeColumnWidthPt, amountColumnWidthPt, minNameColumnWidthPt, horizontalMarginPt } = AS_OF_SERIES_PDF_FIT;
    const availableWidthPt = a4LandscapeWidthPt - 2 * horizontalMarginPt - codeColumnWidthPt - minNameColumnWidthPt;
    return Math.floor(availableWidthPt / amountColumnWidthPt);
}
/** Whether a series of `columnCount` dates fits an A4 landscape PDF. */
export function asOfSeriesPdfFits(columnCount) {
    return columnCount <= maxAsOfSeriesPdfColumns();
}
